/**
 * Subscriptions feature custom hooks
 *
 * These hooks provide a simplified interface for subscription management,
 * combining multiple API queries and adding business logic.
 */

import { useCallback, useMemo, useState } from 'react';
import { useStatsigClient } from '@statsig/react-bindings';
import type { BundlePriceResponse, EntitlementResponse } from '@store/generated/subscriptions';

import type {
  AddOnBundlesConfig,
  BillingInterval,
  BundleType,
  BundleWithStatus,
  SubscriptionFlowType,
} from '../types';
import { DEFAULT_ADDON_BUNDLES_CONFIG } from '../types';

import {
  useGetBundleCatalogQuery,
  useGetEntitlementsQuery,
  useGetSubscriptionQuery,
  useGetUserBundlesQuery,
} from './api';

/**
 * Statuses that require the checkout flow (need payment method)
 */
const CHECKOUT_FLOW_STATUSES = ['trialing', 'past_due', 'canceled', 'unpaid'];

/**
 * Hook to determine which flow to show based on subscription status
 */
export function useSubscriptionFlowType() {
  const { data: subscription, isLoading, error } = useGetSubscriptionQuery();

  const flowType: SubscriptionFlowType = useMemo(() => {
    const status = subscription?.subscription_status;

    // Default to checkout flow if no status or status requires payment
    if (!status || CHECKOUT_FLOW_STATUSES.includes(status)) {
      return 'checkout';
    }

    // Active subscription can modify directly
    return 'modify';
  }, [subscription?.subscription_status]);

  return {
    flowType,
    status: subscription?.subscription_status,
    isCheckoutFlow: flowType === 'checkout',
    isModifyFlow: flowType === 'modify',
    isLoading,
    error,
  };
}

/**
 * Hook to determine if bundling (add-ons/tiers) is enabled for the current user.
 *
 * Derives this from the backend response: if the bundle catalog contains
 * add-on bundles, bundling is enabled. This replaces the client-side Statsig
 * gate `feature_bundling` with a backend-driven check.
 */
export function useBundlingEnabled() {
  const { data, isLoading } = useGetBundleCatalogQuery();

  const isBundlingEnabled = useMemo(() => {
    if (!data?.bundles) return false;
    return data.bundles.some(b => b.bundle_type === 'add_on' && b.purchasable !== false);
  }, [data]);

  // Count purchasable tiers to determine if catalog has only one tier
  const tierCount = useMemo(() => {
    if (!data?.bundles) return 0;
    return data.bundles.filter(b => b.bundle_type === 'tier' && b.purchasable !== false).length;
  }, [data]);

  return {
    isBundlingEnabled,
    isSingleTier: tierCount === 1,
    isLoading,
  };
}

/**
 * Hook for bundle catalog with status flags, split by type
 */
interface UseBundleCatalogOptions {
  skip?: boolean;
}

export function useBundleCatalog(options: UseBundleCatalogOptions = {}) {
  const { skip = false } = options;

  const {
    data: catalogData,
    isLoading: isCatalogLoading,
    error: catalogError,
  } = useGetBundleCatalogQuery(undefined, { skip });

  // All bundles (including non-purchasable) for price lookups in subscription management
  const allBundles: BundleWithStatus[] = useMemo(() => {
    if (!catalogData?.bundles) return [];

    return catalogData.bundles.map((bundle: BundlePriceResponse) => ({
      bundle_id: bundle.bundle_id,
      name: bundle.name,
      description: bundle.description ?? undefined,
      bundle_type: (bundle.bundle_type ?? 'add_on') as BundleType,
      compatible_addon_ids: bundle.compatible_addon_ids ?? undefined,
      monthly_unit_amount: bundle.monthly_unit_amount ?? 0,
      monthly_price_id: bundle.monthly_price_id ?? '',
      yearly_unit_amount: bundle.yearly_unit_amount ?? 0,
      yearly_price_id: bundle.yearly_price_id ?? '',
      currency: bundle.currency ?? 'usd',
      stripe_product_id: bundle.stripe_product_id,
      marketing_features: bundle.marketing_features ?? [],
      grants: bundle.grants?.map(g => ({
        capability_name: g.capability_name ?? '',
        metering_type: g.metering_type,
        quota: g.quota ?? null,
        usage_period: g.usage_period,
      })),
      intro_offer: bundle.intro_offer ?? null,
      isSelected: false,
    }));
  }, [catalogData]);

  // Purchasable bundles only (for checkout/selection UIs)
  const bundles: BundleWithStatus[] = useMemo(() => {
    return allBundles.filter(bundle => {
      const catalogBundle = catalogData?.bundles?.find(b => b.bundle_id === bundle.bundle_id);
      return catalogBundle?.purchasable !== false;
    });
  }, [allBundles, catalogData]);

  // Split bundles by type and sort
  const tiers = useMemo(
    () =>
      bundles
        .filter(b => b.bundle_type === 'tier')
        .sort((a, b) => a.monthly_unit_amount - b.monthly_unit_amount),
    [bundles],
  );

  const addOns = useMemo(
    () =>
      bundles.filter(b => b.bundle_type === 'add_on').sort((a, b) => a.name.localeCompare(b.name)),
    [bundles],
  );

  // Array for the add-on selection UI. Kept as a distinct export name from
  // `addOns` because several call sites consume it; identical now that the
  // org-wide security/data bundle type has been retired.
  const addOnsForDisplay = addOns;

  // Calculate annual discount range from all bundles
  // Returns null if no valid discounts can be calculated
  const annualDiscountRange = useMemo((): { min: number; max: number } | null => {
    // Calculate discount for each bundle that has valid pricing
    const discounts = bundles
      .filter(b => b.monthly_unit_amount > 0 && b.yearly_unit_amount > 0)
      .map(b => {
        const annualAtMonthlyRate = b.monthly_unit_amount * 12;
        return Math.round((1 - b.yearly_unit_amount / annualAtMonthlyRate) * 100);
      })
      .filter(d => d > 0); // Only positive discounts

    if (discounts.length === 0) {
      return null; // No fallback - hide badge if no valid discounts
    }

    return {
      min: Math.min(...discounts),
      max: Math.max(...discounts),
    };
  }, [bundles]);

  // Map of ALL tier bundle_ids to their compatible_addon_ids (including non-purchasable tiers).
  // Used by getCompatibleAddOnsForDisplay to filter add-ons even when the selected
  // tier is non-purchasable (e.g., a member's legacy tier that was filtered from the catalog).
  const allTierCompatibility = useMemo(() => {
    if (!catalogData?.bundles) return new Map<string, string[]>();
    const map = new Map<string, string[]>();
    catalogData.bundles.forEach(b => {
      if (b.bundle_type === 'tier' && b.compatible_addon_ids) {
        map.set(b.bundle_id, b.compatible_addon_ids);
      }
    });
    return map;
  }, [catalogData]);

  /**
   * Get add-ons compatible with the selected tier.
   * @param selectedTierId - The bundle_id of the selected tier, or null
   * @returns Filtered array of compatible add-ons (alphabetical)
   */
  const getCompatibleAddOnsForDisplay = useCallback(
    (selectedTierId: string | null): BundleWithStatus[] => {
      if (!selectedTierId) {
        // If no tier selected, return all add-ons
        return addOnsForDisplay;
      }

      // Look up compatible add-on IDs: first from purchasable tiers, then from
      // all tiers (handles non-purchasable tiers like legacy plans)
      const selectedTier = tiers.find(t => t.bundle_id === selectedTierId);
      const compatibleIds =
        selectedTier?.compatible_addon_ids ?? allTierCompatibility.get(selectedTierId);

      if (!compatibleIds) {
        // Tier exists but has no compatibility list — show all
        return addOnsForDisplay;
      }

      const compatibleAddOnIds = new Set(compatibleIds);

      return addOnsForDisplay
        .filter(bundle => compatibleAddOnIds.has(bundle.bundle_id))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    [tiers, addOnsForDisplay, allTierCompatibility],
  );

  return {
    bundles,
    allBundles, // Includes non-purchasable bundles for price lookups
    tiers,
    addOns,
    addOnsForDisplay,
    annualDiscountRange,
    getCompatibleAddOnsForDisplay,
    isLoading: isCatalogLoading,
    error: catalogError,
  };
}

/**
 * Hook for subscription details
 */
export function useSubscription() {
  const { data, isLoading, error, refetch } = useGetSubscriptionQuery();

  return {
    subscription: data,
    hasSubscription: data?.has_subscription ?? false,
    status: data?.subscription_status,
    hasPaymentMethod: data?.has_payment_method ?? false,
    isLoading,
    error,
    refetch,
  };
}

/**
 * Hook for "My Access" page - combines subscription, bundles, and entitlements
 * Available to all users (not just billing admins)
 */
export function useMyAccess() {
  const {
    subscription,
    hasSubscription,
    status,
    hasPaymentMethod,
    isLoading: isSubscriptionLoading,
    error: subscriptionError,
    refetch: refetchSubscription,
  } = useSubscription();

  // Use query directly instead of useUserBundles to avoid mutation dependencies
  const {
    data: userBundlesData,
    isLoading: isBundlesLoading,
    error: bundlesError,
    refetch: refetchBundles,
  } = useGetUserBundlesQuery();

  const userBundles = userBundlesData?.bundles || [];

  const {
    data: entitlementsData,
    isLoading: isEntitlementsLoading,
    error: entitlementsError,
    refetch: refetchEntitlements,
  } = useGetEntitlementsQuery();

  // Separate tier from add-ons using bundle_type directly from response
  const currentTier = useMemo(() => userBundles.find(b => b.bundle_type === 'tier'), [userBundles]);

  const currentAddOns = useMemo(
    () => userBundles.filter(b => b.bundle_type === 'add_on'),
    [userBundles],
  );

  // Get entitlements list
  const entitlements: EntitlementResponse[] = useMemo(
    () => entitlementsData?.entitlements || [],
    [entitlementsData],
  );

  // Group entitlements by bundle for display
  const entitlementsByBundle = useMemo(() => {
    const grouped = new Map<string, EntitlementResponse[]>();
    for (const entitlement of entitlements) {
      const existing = grouped.get(entitlement.bundle_id) || [];
      grouped.set(entitlement.bundle_id, [...existing, entitlement]);
    }
    return grouped;
  }, [entitlements]);

  // Get entitlements that have usage tracking (metered)
  const meteredEntitlements = useMemo(
    () => entitlements.filter(e => e.quota !== null),
    [entitlements],
  );

  // Combined loading state
  const isLoading = isSubscriptionLoading || isBundlesLoading || isEntitlementsLoading;

  // Combined error state
  const error = subscriptionError || bundlesError || entitlementsError;

  // Refetch all data
  const refetchAll = useCallback(() => {
    refetchSubscription();
    refetchBundles();
    refetchEntitlements();
  }, [refetchSubscription, refetchBundles, refetchEntitlements]);

  return {
    // Subscription info
    subscription,
    hasSubscription,
    status,
    hasPaymentMethod,

    // Bundles
    currentTier,
    currentAddOns,
    userBundles,

    // Entitlements
    entitlements,
    entitlementsByBundle,
    meteredEntitlements,

    // State
    isLoading,
    error,
    refetchAll,
  };
}

/**
 * Hook for bundle selection in the checkout flow
 * Manages tier/add-on selection, billing interval, and checkout
 */
export function useBundleSelection() {
  const { tiers, getCompatibleAddOnsForDisplay, isLoading, error } = useBundleCatalog();
  const [selectedTierId, setSelectedTierId] = useState<string | null>(null);
  const [selectedAddOnIds, setSelectedAddOnIds] = useState<Set<string>>(new Set());
  const [billingInterval, setBillingInterval] = useState<BillingInterval>('year');
  const [isCheckingOut, setIsCheckingOut] = useState(false);

  // Get compatible add-ons for selected tier
  const compatibleAddOns = useMemo(
    () => getCompatibleAddOnsForDisplay(selectedTierId),
    [getCompatibleAddOnsForDisplay, selectedTierId],
  );

  // Get selected bundles with status
  const selectedBundles = useMemo(() => {
    const selected: BundleWithStatus[] = [];

    // Add selected tier
    if (selectedTierId) {
      const tier = tiers.find(t => t.bundle_id === selectedTierId);
      if (tier) {
        selected.push({ ...tier, isSelected: true });
      }
    }

    // Add selected add-ons
    for (const addOn of compatibleAddOns) {
      if (selectedAddOnIds.has(addOn.bundle_id)) {
        selected.push({ ...addOn, isSelected: true });
      }
    }

    return selected;
  }, [tiers, compatibleAddOns, selectedTierId, selectedAddOnIds]);

  // Calculate total based on billing interval
  const totalAmount = useMemo(() => {
    return selectedBundles.reduce((sum, bundle) => {
      const amount =
        billingInterval === 'year' ? bundle.yearly_unit_amount : bundle.monthly_unit_amount;
      return sum + amount;
    }, 0);
  }, [selectedBundles, billingInterval]);

  // Get currency from first selected bundle
  const currency = useMemo(() => {
    return selectedBundles[0]?.currency ?? 'usd';
  }, [selectedBundles]);

  // Can checkout if at least a tier is selected
  const canCheckout = selectedTierId !== null;

  const selectTier = useCallback((bundleId: string) => {
    setSelectedTierId(bundleId);
  }, []);

  const toggleAddOn = useCallback((bundleId: string) => {
    setSelectedAddOnIds(prev => {
      const next = new Set(prev);
      if (next.has(bundleId)) {
        next.delete(bundleId);
      } else {
        next.add(bundleId);
      }
      return next;
    });
  }, []);

  // Checkout function - placeholder, actual implementation would call API
  const checkout = useCallback(async (_paymentMethodId?: string) => {
    setIsCheckingOut(true);
    try {
      // TODO: Implement actual checkout API call
      return { success: true };
    } finally {
      setIsCheckingOut(false);
    }
  }, []);

  return {
    // Catalog data
    tiers: tiers.map(t => ({ ...t, isSelected: t.bundle_id === selectedTierId })),
    addOns: compatibleAddOns.map(a => ({ ...a, isSelected: selectedAddOnIds.has(a.bundle_id) })),
    selectedBundles,

    // Selection actions
    selectTier,
    toggleAddOn,

    // Billing
    billingInterval,
    setBillingInterval,
    totalAmount,
    currency,

    // Checkout
    canCheckout,
    checkout,
    isCheckingOut,

    // Loading/error
    isLoading,
    error,
  };
}

// ============================================================================
// Add-on Bundles Page Config Hook
// ============================================================================

const ADDON_BUNDLES_CONFIG_NAME = 'addon_bundles_config';

/**
 * Hook to get Add-on Bundles page configuration from Statsig dynamic config.
 *
 * This allows the page content (title, tabs, features, FAQ) to be
 * configured remotely without code changes.
 *
 * @returns AddOnBundlesConfig with dynamic values or defaults
 *
 * @example
 * const config = useAddOnBundlesConfig();
 * // Use config.pageTitle, config.tabs, etc.
 */
export function useAddOnBundlesConfig(): AddOnBundlesConfig {
  const { client } = useStatsigClient();

  return useMemo(() => {
    const dynamicConfig = client.getDynamicConfig(ADDON_BUNDLES_CONFIG_NAME);
    const configValue = dynamicConfig.value as Record<string, unknown> | null;

    // Safely extract values with type guards
    const pageTitle =
      configValue && typeof configValue.pageTitle === 'string'
        ? configValue.pageTitle
        : DEFAULT_ADDON_BUNDLES_CONFIG.pageTitle;

    const pageIntro =
      configValue && typeof configValue.pageIntro === 'string'
        ? configValue.pageIntro
        : DEFAULT_ADDON_BUNDLES_CONFIG.pageIntro;

    const tabs =
      configValue && Array.isArray(configValue.tabs)
        ? (configValue.tabs as AddOnBundlesConfig['tabs'])
        : DEFAULT_ADDON_BUNDLES_CONFIG.tabs;

    return {
      pageTitle,
      pageIntro,
      tabs,
    };
  }, [client]);
}
